import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CanActivateFn, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { catchError, filter, from, map, of, switchMap, take } from 'rxjs';
import { UserStatusType } from '../../user/data/user-status-type';
import { getWorkflowBlockingRoute } from '../../user/data/workflow-route';
import {
  selectUserRequestWorkflowStatusTypeId,
  selectUserStatusTypeId,
} from '../../user/store/selectors/user.selectors';
import { AuthService } from '../services/auth/auth.service';

export const authGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const store = inject(Store);

  const userStatusTypeId = store.selectSignal(selectUserStatusTypeId);
  const userRequestWorkflowStatusTypeId = store.selectSignal(selectUserRequestWorkflowStatusTypeId);

  return toObservable(authService.isAuthComplete).pipe(
    filter(ready => ready),
    take(1),
    switchMap(() =>
      from(authService.isAuthenticated()).pipe(
        map(isAuthenticated => {
          const workflowRoute = getWorkflowBlockingRoute(userRequestWorkflowStatusTypeId());
          if (workflowRoute) return router.createUrlTree([workflowRoute]);

          const status = userStatusTypeId();
          // KNOWN FAIL-OPEN, pre-dating PBI 54129 and not closed by it: a null status means the
          // getUser probe never produced one (a 5xx or a network failure), and falling through to
          // `isAuthenticated` admits the user past both the Pending -> assign-patient and
          // !Active -> unauthorized redirects below.
          //
          // 54129 bounded requests at 30s, which would have made this reachable by a merely slow
          // login API — a confident wrong answer rather than a hang. Rather than ratify that,
          // UsersService.getUserByAzureId opts the probe out of the timeout, so a stall leaves
          // isAuthComplete false and blocks navigation instead. The two are a pair; read that
          // method's comment before changing either.
          //
          // Closing the fail-open properly means redirecting (or rendering an explicit "we couldn't
          // verify your account" state) instead of admitting, and needs the Pending and non-Active
          // flows re-tested so a transient backend blip does not hard-block a legitimate patient.
          // That is its own change, not a drive-by tightening here.
          if (status == null) return isAuthenticated;
          if (status === UserStatusType.Pending) return router.createUrlTree(['assign-patient']);
          if (status !== UserStatusType.Active) return router.createUrlTree(['unauthorized']);
          return isAuthenticated;
        }),
        catchError(() => of(false))
      )
    )
  );
};
